import { Check } from '@mui/icons-material'
import {
  Box,
  Grid,
  SelectChangeEvent,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mousewheel, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { CustomSelect } from '../../components/CustomSelect'
import Progress from '../../components/Progress'
import StepsBtn from '../../components/StepsBtn'
import TextInformation from '../../components/TextInformation'
import { useApp } from '../../hooks/useApp'
import '../../i18n'
import { InstallationsTypes } from '../../types/Views'
import { useImages, useImagesUrls } from '../../queries/useUtilQueries'
import { setFormDataKey, useFormData } from '../../queries/userGlobalQueries'
import { useSpaces } from '../../queries/useCompanyQueries'
import { queryClient } from '../../queries/config'

const ImageListItemStyled = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  '& img': {
    transition: 'all 0.5s ease',
    '&:hover': { transform: 'scale(1.07)' },
    objectPosition: 'center'
  }
}))

const BoxSelectsStyled = styled(Box)(({ theme }) => ({
  border: `solid 2px ${grey[200]}`,
  padding: theme.spacing(5),
  margin: theme.spacing(6, 0, 3),
  width: '100%',
  maxWidth: 500,
  rowGap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    border: 'none',
    margin: 0,
    minWidth: 300,
    paddingTop: 15
  }
}))

const LightboxStyled = styled(Lightbox)(() => ({
  '& .yarl__container': {
    backgroundColor: '#000000b0'
  },
  '& img': {
    width: '600px !important',
    height: '400px !important',
    maxWidth: '600px !important',
    maxHeight: '400px !important'
  }
}))

const Installations = ({
  loading: externalLoading,
  handleNext,
  isFirstLoad,
  setIsFirstLoad
}: InstallationsTypes) => {
  const theme = useTheme()
  const { iDCompany } = useApp()
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const isXl = useMediaQuery(theme.breakpoints.up('xl'))
  const { t } = useTranslation()

  const [selectDescription, setSelectDescription] = useState<string[]>([])
  const [open, setOpenLightbox] = useState<boolean>(false)
  const [indexImg, setIndexImg] = useState<number>(0)
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([])

  const { data: areasData } = useSpaces()
  const { data: formData } = useFormData()

  // Queries para imágenes
  const { data: imagesData, isLoading: isLoadingImages } = useImages(
    iDCompany?.body?.content[0]?.id || '',
    areasData?.body[formData.area]?.id || '',
    formData.desc
      ? areasData?.body[formData.area]?.areaDetailList?.find(
          (item: any) => item.description === formData.desc
        )?.id
      : undefined
  )

  const { data: imageUrls, isLoading: isLoadingUrls } = useImagesUrls(selectedImageIds)

  useEffect(() => {
    if (imagesData?.body) {
      const filteredPhotos = imagesData.body.filter((item: any) => item.idDocumentCategory.id === 1)
      setSelectedImageIds(filteredPhotos.map((item: any) => item.id))
    }
  }, [imagesData])

  // Efecto para cargar datos iniciales desde URL
  useEffect(() => {
    const loadFromUrl = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const origenParam = urlParams.get('origen')
      const destinoParam = urlParams.get('destino')

      if (origenParam && areasData?.body) {
        const selectedArea = areasData.body[origenParam]
        if (!selectedArea) return

        const areaDetailList = selectedArea.areaDetailList
        const firstAreaDetail = areaDetailList ? areaDetailList[0] : null
        const areaType = firstAreaDetail ? firstAreaDetail.areaType : ''

        setSelectDescription(areaDetailList?.map((item: any) => item.description) || [])

        queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
          ...old,
          area: origenParam,
          areaType: areaType,
          desc: destinoParam || '',
          detailsArea: destinoParam
            ? areaDetailList?.find((item: any) => item.description === destinoParam)?.details || ''
            : '',
          bookingDay: '',
          hours: []
        }))
      }
    }

    if (areasData?.body) {
      loadFromUrl()
    }
  }, [areasData])

  // Efecto para inicializar selectDescription cuando se monta el componente o cambia formData.area
  useEffect(() => {
    if (formData.area && areasData?.body) {
      const selectedArea = areasData.body[formData.area]
      if (selectedArea && selectedArea.areaDetailList) {
        setSelectDescription(selectedArea.areaDetailList.map((item: any) => item.description) || [])
      }
    }
  }, [formData.area, areasData])

  // Efecto para scroll inicial
  useEffect(() => {
    if (isFirstLoad) {
      document.documentElement.scrollTop = 0
      setIsFirstLoad?.(false)
    } else {
      document.documentElement.scrollTop = 625
    }
  }, [isFirstLoad, setIsFirstLoad])

  // Manejador de cambios en selects
  const handleChange = (event: SelectChangeEvent, typeSelect: string) => {
    const value = event.target.value as string

    if (typeSelect === 'areas') {
      const selectedArea = areasData?.body[value]
      if (!selectedArea) return

      const areaDetailList = selectedArea.areaDetailList
      const firstAreaDetail = areaDetailList ? areaDetailList[0] : null
      const type = selectedArea.type

      setSelectDescription(areaDetailList?.map((item: any) => item.description) || [])

      queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
        ...old,
        area: value,
        areaType: type,
        desc: areaDetailList?.length === 1 ? areaDetailList[0].description : '',
        detailsArea: areaDetailList?.length === 1 ? areaDetailList[0].details : '',
        bookingDay: '',
        hours: []
      }))
    } else {
      const selectedArea = areasData?.body[formData.area]
      if (!selectedArea) return

      const selectedSubarea = selectedArea.areaDetailList?.find(
        (item: any) => item.description === value
      )

      queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
        ...old,
        desc: value,
        detailsArea: selectedSubarea?.details || '',

        selectedSubarea: selectedSubarea || {},
        bookingDay: '',
        hours: []
      }))
    }
  }

  const handleLightbox = (index: number) => {
    setOpenLightbox(true)
    setIndexImg(index)
  }

  const isLoading = externalLoading || isLoadingImages || isLoadingUrls

  return (
    <>
      {isLoading ? (
        <Progress />
      ) : areasData?.status === 200 ? (
        <>
          <Grid container columnSpacing={4} sx={{ height: '100%' }}>
            <Grid
              item
              xs={12}
              lg={8}
              sx={{
                borderRight: {
                  lg: '1px solid'
                },
                pr: 3
              }}
            >
              {(isMd || isXl) && <TextInformation title={`${t('selectInstallation')}`} />}

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <BoxSelectsStyled>
                  <CustomSelect
                    name="area"
                    label=""
                    disabledEmpty
                    listItems={
                      areasData?.body?.areas
                        ? [...areasData.body.areas].sort((a, b) => a.localeCompare(b))
                        : []
                    }
                    value={formData.area}
                    onChange={(e: SelectChangeEvent) => handleChange(e, 'areas')}
                  />
                  <Box hidden={formData.area === ''} sx={{ mt: 3 }}>
                    <CustomSelect
                      name="desc"
                      label=""
                      disabledEmpty
                      listItems={[...selectDescription].sort((a, b) => a.localeCompare(b))}
                      value={formData.desc}
                      onChange={(e: SelectChangeEvent) => handleChange(e, 'description')}
                    />
                  </Box>
                </BoxSelectsStyled>
              </Stack>
              <Stack
                key={formData.desc}
                direction={{ xs: 'column', md: 'row' }}
                alignItems={'center'}
                justifyContent={'center'}
                sx={{ maxWidth: '100%', width: '600px', margin: '0 auto' }}
              >
                <Grid container>
                  {formData.detailsArea !== '' &&
                    formData.detailsArea
                      .split(/\s*,\s*/)
                      .map((item: string) => item.trim())
                      .filter(Boolean)
                      .map((item: string, i: number) => (
                        <Grid item xs={12} sm={6} lg={4} key={i}>
                          <Typography
                            variant="body1"
                            sx={{ mr: 1.5, mb: 1, display: 'flex', alignItems: 'center' }}
                          >
                            <Check
                              color="primary"
                              sx={{
                                verticalAlign: 'text-bottom',
                                mr: 0.5,
                                fontSize: '1.25rem'
                              }}
                            />
                            {item.trim().replace(/^[,\s]+|[,\s]+$/g, '')}
                          </Typography>
                        </Grid>
                      ))}
                </Grid>
              </Stack>
              <StepsBtn
                disabledBack={true}
                onClickNext={handleNext}
                disabledNext={formData.area === '' || formData.desc.length === 0}
              />
            </Grid>
            <Grid
              item
              xs={12}
              lg={4}
              sx={{
                overflow: 'hidden',
                pt: '20px!important',
                display: 'flex',
                flexDirection: 'column',
                pb: '15px'
              }}
            >
              <Stack
                sx={{
                  maxHeight: {
                    lg: '52vh'
                  }
                }}
              >
                {formData.area &&
                  imageUrls &&
                  (imageUrls.length > 2 ? (
                    <Swiper
                      spaceBetween={10}
                      slidesPerView={1}
                      navigation
                      mousewheel={true}
                      direction={'horizontal'}
                      modules={[Navigation, Mousewheel]}
                      breakpoints={{
                        425: {
                          slidesPerView: 2,
                          direction: 'horizontal'
                        },
                        600: {
                          slidesPerView: 3,
                          direction: 'horizontal'
                        },
                        1201: {
                          slidesPerView: 3,
                          direction: 'vertical'
                        }
                      }}
                    >
                      {imageUrls.map((img, i) => (
                        <SwiperSlide key={i}>
                          <ImageListItemStyled>
                            <img alt="name" src={img} onClick={() => handleLightbox(i)} />
                          </ImageListItemStyled>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  ) : (
                    imageUrls.map((img, i) => (
                      <ImageListItemStyled key={i}>
                        <img
                          alt="name"
                          height="175px"
                          src={img}
                          onClick={() => handleLightbox(i)}
                          className="img-without-slider"
                        />
                      </ImageListItemStyled>
                    ))
                  ))}
              </Stack>
            </Grid>
          </Grid>
          <LightboxStyled
            open={open}
            index={indexImg}
            close={() => setOpenLightbox(false)}
            slides={imageUrls?.map((url) => ({ src: url })) || []}
            carousel={{ finite: true }}
            controller={{ closeOnBackdropClick: true }}
          />
        </>
      ) : (
        <Typography variant="h4" color="error" align="center" fontWeight={600} my={20}>
          {areasData
            ? `Error ${areasData?.status}: ${areasData?.statusText}`
            : 'Error, web no tiene data cargada'}
        </Typography>
      )}
    </>
  )
}

export default Installations
